import { useMemo, useState } from "react";
import { ArrowUpRight, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { useGetList, useLocaleState, useTranslate } from "ra-core";
import { useSearchParams } from "react-router";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Client, ContentPiece, PieceFormat, PieceStatus } from "../types";
import { MonthGrid } from "./MonthGrid";
import { PieceDetail } from "./PieceDetail";
import { PieceFormDialog } from "./PieceFormDialog";
import {
  FORMAT_ICONS,
  PIECE_FORMATS,
  STATUS_COLORS,
  addMonths,
  monthRange,
  parseMonthKey,
  toMonthKey,
} from "./pieceModel";

const ALL_CLIENTS = "all";

export const CalendarPage = () => {
  const translate = useTranslate();
  const [locale = "es"] = useLocaleState();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCreating, setIsCreating] = useState(false);

  const month = parseMonthKey(searchParams.get("month"));
  const clientId = searchParams.get("client") ?? ALL_CLIENTS;
  const selectedPieceId = searchParams.get("piece");
  const statusFilter = searchParams.get("status") as PieceStatus | null;
  const formatFilter = searchParams.get("format") as PieceFormat | null;

  const updateParams = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(changes).forEach(([key, value]) =>
      value == null ? next.delete(key) : next.set(key, value),
    );
    setSearchParams(next, { replace: true });
  };

  const { data: clients = [], isPending: isLoadingClients } =
    useGetList<Client>("clients", {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "name", order: "ASC" },
    });

  const { from, to } = monthRange(month);
  const { data: monthPieces = [] } = useGetList<ContentPiece>(
    "content_pieces",
    {
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "publish_date", order: "ASC" },
      filter: {
        "publish_date@gte": from,
        "publish_date@lte": to,
        ...(clientId !== ALL_CLIENTS ? { company_id: clientId } : {}),
      },
    },
  );

  // Pieces still without a publish date belong to no month: list them apart
  // so they never disappear from the calendar.
  const { data: undatedPieces = [] } = useGetList<ContentPiece>(
    "content_pieces",
    {
      pagination: { page: 1, perPage: 200 },
      sort: { field: "created_at", order: "DESC" },
      filter: {
        "publish_date@is": "null",
        ...(clientId !== ALL_CLIENTS ? { company_id: clientId } : {}),
      },
    },
  );

  const matchesFilters = (piece: ContentPiece) =>
    (!statusFilter || piece.status === statusFilter) &&
    (!formatFilter || piece.format === formatFilter);

  const visiblePieces = useMemo(
    () => monthPieces.filter(matchesFilters),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [monthPieces, statusFilter, formatFilter],
  );
  const visibleUndated = undatedPieces.filter(matchesFilters);
  const allPieces = [...monthPieces, ...undatedPieces];

  const clientNames = useMemo(
    () => new Map(clients.map((client) => [String(client.id), client.name])),
    [clients],
  );

  const countBy = (status: PieceStatus) =>
    allPieces.filter((piece) => piece.status === status).length;

  const monthLabel = month.toLocaleDateString(
    locale === "es" ? "es-CO" : locale,
    { month: "long", year: "numeric" },
  );

  return (
    <div className="mt-6 flex flex-col gap-5 pb-8">
      <div className="flex flex-wrap items-center gap-3">
        <Select
          value={clientId}
          onValueChange={(value) =>
            updateParams({ client: value, piece: null })
          }
        >
          <SelectTrigger className="h-11 min-w-56 rounded-full bg-card px-4">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_CLIENTS}>
              {translate("resources.content_pieces.all_clients")}
            </SelectItem>
            {clients.map((client) => (
              <SelectItem key={client.id} value={String(client.id)}>
                {client.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex items-center gap-1.5">
          <RoundButton
            label="previous"
            onClick={() =>
              updateParams({ month: toMonthKey(addMonths(month, -1)) })
            }
          >
            <ChevronLeft className="size-4" />
          </RoundButton>
          <RoundButton
            label="next"
            onClick={() =>
              updateParams({ month: toMonthKey(addMonths(month, 1)) })
            }
          >
            <ChevronRight className="size-4" />
          </RoundButton>
        </div>
        <h1 className="text-3xl font-semibold tracking-tight first-letter:uppercase">
          {monthLabel}
        </h1>

        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setIsCreating(true)}
          disabled={clients.length === 0}
          className="flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"
        >
          <Plus className="size-4" />
          {translate("resources.content_pieces.action.new")}
        </button>
      </div>

      {!isLoadingClients && clients.length === 0 ? (
        <p className="rounded-[22px] bg-card p-6 text-muted-foreground">
          {translate("resources.content_pieces.no_clients")}
        </p>
      ) : null}

      <div className="grid gap-3.5 grid-cols-2 lg:grid-cols-4">
        <StatCard
          label={translate("resources.content_pieces.stats.total")}
          value={allPieces.length}
          color="var(--muted-foreground)"
          active={!statusFilter}
          onClick={() => updateParams({ status: null })}
        />
        {(["in_review", "changes_requested", "approved"] as PieceStatus[]).map(
          (status) => (
            <StatCard
              key={status}
              label={translate(`resources.content_pieces.statuses.${status}`)}
              value={countBy(status)}
              color={STATUS_COLORS[status]}
              highlight={status === "in_review"}
              active={statusFilter === status}
              onClick={() =>
                updateParams({
                  status: statusFilter === status ? null : status,
                })
              }
            />
          ),
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Chip
          active={!formatFilter}
          onClick={() => updateParams({ format: null })}
        >
          {translate("resources.content_pieces.filters.all")}
        </Chip>
        {PIECE_FORMATS.map((format) => {
          const Icon = FORMAT_ICONS[format];
          return (
            <Chip
              key={format}
              active={formatFilter === format}
              onClick={() =>
                updateParams({
                  format: formatFilter === format ? null : format,
                })
              }
            >
              <Icon className="size-3.5" />
              {translate(`resources.content_pieces.formats.${format}`)}
            </Chip>
          );
        })}
      </div>

      {visibleUndated.length > 0 ? (
        <section className="rounded-[22px] bg-card p-5">
          <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {translate("resources.content_pieces.undated", {
              smart_count: visibleUndated.length,
            })}
          </h2>
          <div className="flex flex-wrap gap-2">
            {visibleUndated.map((piece) => {
              const Icon = FORMAT_ICONS[piece.format];
              return (
                <button
                  key={piece.id}
                  type="button"
                  onClick={() => updateParams({ piece: String(piece.id) })}
                  className={`flex max-w-full items-center gap-2 rounded-full border px-3 py-1.5 text-sm hover:bg-accent ${
                    String(piece.id) === selectedPieceId
                      ? "border-primary"
                      : "border-border"
                  }`}
                >
                  <span
                    className="size-2 shrink-0 rounded-full"
                    style={{ background: STATUS_COLORS[piece.status] }}
                  />
                  <Icon className="size-3.5 shrink-0 text-muted-foreground" />
                  <span className="truncate">{piece.title}</span>
                  {clientId === ALL_CLIENTS ? (
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {clientNames.get(String(piece.company_id))}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </section>
      ) : null}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
        <MonthGrid
          month={month}
          pieces={visiblePieces}
          selectedPieceId={selectedPieceId}
          clientNames={clientId === ALL_CLIENTS ? clientNames : undefined}
          onSelect={(pieceId) => updateParams({ piece: String(pieceId) })}
        />
        <PieceDetail
          pieceId={selectedPieceId}
          clientName={(id) => clientNames.get(String(id))}
          onDeleted={() => updateParams({ piece: null })}
        />
      </div>

      {isCreating ? (
        <PieceFormDialog
          clients={clients}
          defaultClientId={clientId !== ALL_CLIENTS ? clientId : undefined}
          onClose={() => setIsCreating(false)}
          onSaved={(pieceId) => {
            setIsCreating(false);
            updateParams({ piece: String(pieceId) });
          }}
        />
      ) : null}
    </div>
  );
};

CalendarPage.path = "/calendar";

const RoundButton = ({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    className="grid size-10 place-items-center rounded-full border border-border text-muted-foreground hover:text-foreground"
  >
    {children}
  </button>
);

const StatCard = ({
  label,
  value,
  color,
  highlight = false,
  active,
  onClick,
}: {
  label: string;
  value: number;
  color: string;
  highlight?: boolean;
  active: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`relative rounded-[22px] p-5 text-left transition-colors ${
      highlight
        ? "bg-primary text-primary-foreground"
        : "bg-card hover:bg-accent"
    } ${active && !highlight ? "ring-1 ring-primary" : ""}`}
  >
    <span className="flex items-center gap-2 text-sm opacity-80">
      <span
        className="size-2 rounded-full"
        style={{ background: highlight ? "#fff" : color }}
      />
      {label}
    </span>
    <span className="mt-3 block text-4xl font-semibold leading-none tracking-tight">
      {value}
    </span>
    <span
      className={`absolute -top-1.5 -right-1.5 grid size-11 place-items-center rounded-full border-[6px] border-background ${
        highlight ? "bg-white text-primary" : "bg-card text-muted-foreground"
      }`}
    >
      <ArrowUpRight className="size-4" />
    </span>
  </button>
);

const Chip = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm transition-colors ${
      active
        ? "border-foreground bg-foreground font-semibold text-background"
        : "border-border text-muted-foreground hover:text-foreground"
    }`}
  >
    {children}
  </button>
);
